/**
 * contractBalance.ts — BALANCE ENGINE REAL
 *
 * Single source of truth for ALL contract financial calculations.
 *
 * Grand Total = base_total + addon_charges (source='addon', status='active')
 *             + manual_charges (source='manual', status='active')
 * Total Paid  = sum(payments where status IN ('confirmed', 'completed', 'paid'))
 * Balance     = Grand Total - Total Paid
 *
 * payment_status rules:
 *   - overpaid       → totalPaid > grandTotal
 *   - paid           → totalPaid >= grandTotal (and > 0)
 *   - deposit_paid   → totalPaid >= depositAmount AND balance > 0
 *   - partially_paid → totalPaid > 0 AND totalPaid < depositAmount
 *   - unpaid         → totalPaid = 0
 *
 * Manual statuses never auto-overridden: 'refunded', 'cancelled'
 */

import { supabase } from "@/lib/supabaseClient";

/* ─────────────────────────────────────────────
   Types
───────────────────────────────────────────── */

export interface ContractCharge {
  id: string;
  title: string;
  amount: number;
  status: string;
  source: string;
  created_at: string;
}

export interface ContractPaymentRow {
  id: string;
  payment_date: string;
  amount: number;
  method: string;
  reference: string;
  notes: string;
  status: string;
}

export type PaymentStatusKey =
  | "unpaid"
  | "deposit_paid"
  | "partially_paid"
  | "paid"
  | "overpaid"
  | "refunded"
  | "cancelled";

export interface ContractBalanceSummary {
  /** Original contract total (contracts.total_amount) */
  baseTotal: number;
  /** Deposit amount (contracts.deposit_amount) */
  depositAmount: number;
  /** Sum of active addon charges */
  addonTotal: number;
  /** Sum of active manual charges */
  manualTotal: number;
  /** baseTotal + addonTotal + manualTotal */
  grandTotal: number;
  /** Sum of confirmed/completed/paid payments */
  totalPaid: number;
  /** grandTotal - totalPaid (min 0) */
  balanceRemaining: number;
  /** Derived status key */
  paymentStatus: PaymentStatusKey;
  /** All charges */
  charges: ContractCharge[];
  /** Addon-only charges */
  addonCharges: ContractCharge[];
  /** Manual charges */
  manualCharges: ContractCharge[];
  /** Payment rows used in calculation */
  payments: ContractPaymentRow[];
}

/* ─────────────────────────────────────────────
   Core: derive payment status
───────────────────────────────────────────── */

/**
 * Pure function — derive payment_status from numbers.
 * Preserves 'refunded' and 'cancelled' as manual-only states.
 */
export function derivePaymentStatus(
  totalPaid: number,
  grandTotal: number,
  depositAmount: number,
  currentStatus?: string,
): PaymentStatusKey {
  // Never auto-override manual states
  if (currentStatus === "refunded" || currentStatus === "cancelled") {
    return currentStatus as PaymentStatusKey;
  }

  const paid    = Number(totalPaid);
  const total   = Number(grandTotal);
  const deposit = Number(depositAmount);

  if (paid <= 0)              return "unpaid";
  if (total > 0 && paid > total)  return "overpaid";
  if (total > 0 && paid >= total) return "paid";
  if (deposit > 0 && paid >= deposit) return "deposit_paid";
  if (paid > 0)               return "partially_paid";
  return "unpaid";
}

/* ─────────────────────────────────────────────
   Core: full balance summary (fetches from DB)
───────────────────────────────────────────── */

/**
 * Fetch and calculate the full balance for a contract.
 * This is the single entry point for all financial calculations.
 */
export async function getContractBalanceSummary(
  tenantId: string,
  contractId: string,
  baseTotal: number,
  depositAmount: number = 0,
  currentPaymentStatus?: string,
): Promise<ContractBalanceSummary> {
  const [chargesRes, paymentsRes] = await Promise.all([
    supabase
      .from("contract_charges")
      .select("id, title, amount, status, source, created_at")
      .eq("tenant_id", tenantId)
      .eq("contract_id", contractId)
      .order("created_at", { ascending: true }),
    supabase
      .from("payments")
      .select("id, payment_date, amount, method, reference, notes, status")
      .eq("tenant_id", tenantId)
      .eq("contract_id", contractId)
      .order("payment_date", { ascending: false }),
  ]);

  const charges  = (chargesRes.data  ?? []) as ContractCharge[];
  const payments = (paymentsRes.data ?? []) as ContractPaymentRow[];

  const activeCharges  = charges.filter((c) => c.status === "active");
  const addonCharges   = activeCharges.filter((c) => c.source === "addon");
  const manualCharges  = activeCharges.filter((c) => c.source === "manual");

  const addonTotal  = addonCharges.reduce((s, c) => s + Number(c.amount), 0);
  const manualTotal = manualCharges.reduce((s, c) => s + Number(c.amount), 0);
  const grandTotal  = Number(baseTotal) + addonTotal + manualTotal;

  const totalPaid = payments
    .filter((p) =>
      p.status === "confirmed" ||
      p.status === "completed" ||
      p.status === "paid",
    )
    .reduce((s, p) => s + Number(p.amount), 0);

  const balanceRemaining = Math.max(0, grandTotal - totalPaid);
  const paymentStatus = derivePaymentStatus(
    totalPaid,
    grandTotal,
    Number(depositAmount),
    currentPaymentStatus,
  );

  return {
    baseTotal:        Number(baseTotal),
    depositAmount:    Number(depositAmount),
    addonTotal,
    manualTotal,
    grandTotal,
    totalPaid,
    balanceRemaining,
    paymentStatus,
    charges,
    addonCharges,
    manualCharges,
    payments,
  };
}

/* ─────────────────────────────────────────────
   Convenience: calculate + sync to DB
───────────────────────────────────────────── */

/**
 * calculateContractBalance — main entry point.
 *
 * Fetches the contract row, calculates full balance,
 * and optionally syncs balance_amount + payment_status back to DB.
 *
 * Returns full ContractBalanceSummary.
 */
export async function calculateContractBalance(
  tenantId: string,
  contractId: string,
  options: { sync?: boolean } = { sync: true },
): Promise<ContractBalanceSummary | null> {
  // 1. Fetch contract base data
  const { data: contract, error } = await supabase
    .from("contracts")
    .select("id, total_amount, deposit_amount, payment_status")
    .eq("id", contractId)
    .eq("tenant_id", tenantId)
    .maybeSingle();

  if (error || !contract) return null;

  const c = contract as {
    id: string;
    total_amount: number;
    deposit_amount: number;
    payment_status: string;
  };

  // 2. Calculate
  const summary = await getContractBalanceSummary(
    tenantId,
    contractId,
    c.total_amount,
    c.deposit_amount,
    c.payment_status,
  );

  // 3. Sync to DB if requested
  if (options.sync !== false) {
    const updates: Record<string, unknown> = {
      balance_amount: summary.balanceRemaining,
      updated_at:     new Date().toISOString(),
    };
    // Only update payment_status if it changed
    if (summary.paymentStatus !== c.payment_status) {
      updates.payment_status = summary.paymentStatus;
    }
    await supabase
      .from("contracts")
      .update(updates)
      .eq("id", contractId)
      .eq("tenant_id", tenantId);
  }

  return summary;
}

/* ─────────────────────────────────────────────
   Batch: recalculate all contracts for a tenant
   (used in contracts list page)
───────────────────────────────────────────── */

/**
 * Recalculate payment_status for a list of contracts using
 * pre-fetched payment totals (no extra DB calls per contract).
 *
 * Returns the contracts array with updated payment_status.
 */
export function applyPaymentStatusBatch<
  T extends {
    id: string;
    total_amount: number;
    deposit_amount: number;
    payment_status: string;
  },
>(
  contracts: T[],
  paidByContract: Record<string, number>,
): T[] {
  return contracts.map((c) => {
    const totalPaid = paidByContract[c.id] ?? 0;
    const newStatus = derivePaymentStatus(
      totalPaid,
      Number(c.total_amount),
      Number(c.deposit_amount),
      c.payment_status,
    );
    return { ...c, payment_status: newStatus };
  });
}

/* ─────────────────────────────────────────────
   Legacy compat: syncContractBalance
───────────────────────────────────────────── */

/**
 * @deprecated Use calculateContractBalance instead.
 * Kept for backward compatibility during migration.
 */
export async function syncContractBalance(
  tenantId: string,
  contractId: string,
  baseTotal: number,
): Promise<number> {
  const summary = await getContractBalanceSummary(tenantId, contractId, baseTotal);
  await supabase
    .from("contracts")
    .update({
      balance_amount: summary.balanceRemaining,
      updated_at:     new Date().toISOString(),
    })
    .eq("id", contractId)
    .eq("tenant_id", tenantId);
  return summary.balanceRemaining;
}